import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Alert, AlertTitle, Box, Button, CircularProgress, Stack } from '@mui/material';
import i18n from '@/i18n';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Keeps a screen that crashed from taking the whole app with it: the header
 * and menu stay usable, and Reload is the one-click way out — including after
 * a deploy, when the page's code chunk no longer exists on the server.
 *
 * Mounted with the route as its key, so moving to another screen clears it.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    // A class component cannot use the hook, and the language is already loaded by now.
    const t = i18n.t.bind(i18n);
    return (
      <Box sx={{ py: 4 }}>
        <Alert
          severity="error"
          action={
            <Stack direction="row" spacing={1}>
              <Button color="inherit" size="small" onClick={() => this.setState({ error: null })}>
                {t('Try again')}
              </Button>
              <Button color="inherit" size="small" variant="outlined" onClick={() => window.location.reload()}>
                {t('Reload')}
              </Button>
            </Stack>
          }
        >
          <AlertTitle>{t('This screen stopped working')}</AlertTitle>
          {t('Something went wrong while showing this page. Reloading usually fixes it; nothing you saved is lost.')}
        </Alert>
      </Box>
    );
  }
}

/** What a screen shows while its code chunk loads. */
export function PageFallback() {
  return (
    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 240 }}>
      <CircularProgress />
    </Box>
  );
}
